import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { CdcExtraction, CdcBatchRun, CdcSupplementalRecord } from '@/types';
import {
  Globe,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  RefreshCw,
  Filter,
  Play,
  Upload,
  Loader2,
  Search,
  Layers,
  FileText,
  Mountain,
  Droplets,
} from 'lucide-react';

const statusConfig: Record<string, { label: string; color: string; icon: any }> = {
  processing: {
    label: 'Processing',
    color: 'bg-blue-100 text-blue-800',
    icon: Clock,
  },
  completed: {
    label: 'Completed',
    color: 'bg-green-100 text-green-800',
    icon: CheckCircle,
  },
  failed: {
    label: 'Failed',
    color: 'bg-red-100 text-red-800',
    icon: XCircle,
  },
  not_found: {
    label: 'Not Found',
    color: 'bg-gray-100 text-gray-800',
    icon: AlertCircle,
  },
};

const batchStatusConfig: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-800' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800' },
};

type TabType = 'extractions' | 'batches' | 'supplemental';

export default function CdcExtractionsPage() {
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('extractions');

  // Extractions state
  const [extractions, setExtractions] = useState<CdcExtraction[]>([]);
  const [extractionsTotal, setExtractionsTotal] = useState(0);
  const [extractionsPage, setExtractionsPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [isLoadingExtractions, setIsLoadingExtractions] = useState(true);

  // Batch state
  const [batches, setBatches] = useState<CdcBatchRun[]>([]);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);

  // Supplemental state
  const [supplementalData, setSupplementalData] = useState<CdcSupplementalRecord[]>([]);
  const [supplementalFilter, setSupplementalFilter] = useState<string>('');
  const [isLoadingSupplemental, setIsLoadingSupplemental] = useState(false);

  // Missing countries state
  const [missingCountries, setMissingCountries] = useState<{ country_iso2: string; country_name: string }[]>([]);
  const [isLoadingMissing, setIsLoadingMissing] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // Action state
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isTriggeringCountry, setIsTriggeringCountry] = useState(false);
  const [isTriggeringBatch, setIsTriggeringBatch] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [countryInput, setCountryInput] = useState('');
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDataType, setUploadDataType] = useState<string>('altitude_sickness');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadCsvContent, setUploadCsvContent] = useState('');

  // Fetch extractions
  const fetchExtractions = async () => {
    setIsLoadingExtractions(true);
    setError('');
    try {
      const params: any = { page: extractionsPage, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (searchQuery) params.search = searchQuery;
      const data = await api.getCdcExtractions(params);
      setExtractions(data.extractions || []);
      setExtractionsTotal(data.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load extractions');
    } finally {
      setIsLoadingExtractions(false);
    }
  };

  const fetchBatches = async () => {
    setIsLoadingBatches(true);
    try {
      const data = await api.getCdcBatchRuns();
      setBatches(data.batches || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load batch runs');
    } finally {
      setIsLoadingBatches(false);
    }
  };

  const fetchSupplemental = async () => {
    setIsLoadingSupplemental(true);
    try {
      const data = await api.getCdcSupplementalData(supplementalFilter || undefined);
      setSupplementalData(data.supplemental_data || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load supplemental data');
    } finally {
      setIsLoadingSupplemental(false);
    }
  };

  useEffect(() => {
    fetchExtractions();
  }, [statusFilter, searchQuery, extractionsPage]);

  const fetchMissingCountries = async () => {
    setIsLoadingMissing(true);
    try {
      const data = await api.getCdcMissingCountries();
      setMissingCountries(data.missing_countries || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load missing countries');
    } finally {
      setIsLoadingMissing(false);
    }
  };

  const handleRetryMissing = async () => {
    setIsRetrying(true);
    setError('');
    try {
      const data = await api.retryCdcMissingCountries();
      setSuccessMessage(data.message || 'Retry triggered');
      fetchBatches();
      fetchMissingCountries();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to retry');
    } finally {
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'batches') {
      fetchBatches();
      fetchMissingCountries();
    }
    if (activeTab === 'supplemental') fetchSupplemental();
  }, [activeTab, supplementalFilter]);

  // Auto-clear success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleTriggerCountry = async () => {
    if (!countryInput.trim()) return;
    setIsTriggeringCountry(true);
    setError('');
    try {
      const data = await api.triggerCdcExtraction(countryInput.trim().toUpperCase());
      setSuccessMessage(data.message || `Extraction triggered for ${countryInput.toUpperCase()}`);
      setShowTriggerModal(false);
      setCountryInput('');
      setTimeout(() => fetchExtractions(), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to trigger extraction');
    } finally {
      setIsTriggeringCountry(false);
    }
  };

  const handleTriggerBatch = async () => {
    setIsTriggeringBatch(true);
    setError('');
    try {
      const data = await api.triggerCdcBatchExtraction();
      setSuccessMessage(data.message || 'Batch extraction triggered');
      setShowBatchModal(false);
      setActiveTab('batches');
      setTimeout(() => fetchBatches(), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to trigger batch');
    } finally {
      setIsTriggeringBatch(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setUploadCsvContent(ev.target?.result as string);
    };
    reader.readAsText(file);
  };

  const handleUploadCsv = async () => {
    if (!uploadCsvContent) return;
    setIsUploading(true);
    setError('');
    try {
      const data = await api.uploadCdcCsv(uploadDataType, uploadCsvContent);
      setSuccessMessage(data.message || 'CSV uploaded successfully');
      setShowUploadModal(false);
      setUploadCsvContent('');
      setUploadFileName('');
      setActiveTab('supplemental');
      fetchSupplemental();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to upload CSV');
    } finally {
      setIsUploading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const totalPages = Math.ceil(extractionsTotal / 20);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">CDC Extractions</h1>
          <p className="text-gray-500 mt-1">
            AI-powered travel health data extraction from CDC pages
          </p>
        </div>
        <div className="flex flex-wrap gap-2 mt-4 sm:mt-0">
          <button
            onClick={() => setShowTriggerModal(true)}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            <Play size={16} className="mr-2" />
            Extract Country
          </button>
          <button
            onClick={() => setShowBatchModal(true)}
            className="inline-flex items-center px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors text-sm font-medium"
          >
            <Layers size={16} className="mr-2" />
            Extract All
          </button>
          <button
            onClick={() => setShowUploadModal(true)}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <Upload size={16} className="mr-2" />
            Upload CSV
          </button>
        </div>
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <CheckCircle className="text-green-500" size={20} />
            <p className="text-green-700">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          {([
            { key: 'extractions', label: 'Extractions', icon: Globe },
            { key: 'batches', label: 'Batch Runs', icon: Layers },
            { key: 'supplemental', label: 'Supplemental Data', icon: FileText },
          ] as const).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center space-x-2 pb-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Extractions Tab */}
      {activeTab === 'extractions' && (
        <>
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSearchQuery(searchInput);
                      setExtractionsPage(1);
                    }
                  }}
                  placeholder="Search country..."
                  className="pl-9 pr-8 py-2 w-52 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {searchInput && (
                  <button
                    onClick={() => { setSearchInput(''); setSearchQuery(''); setExtractionsPage(1); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XCircle size={16} />
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: '', label: 'All' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'processing', label: 'Processing' },
                  { value: 'failed', label: 'Failed' },
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => { setStatusFilter(filter.value); setExtractionsPage(1); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      statusFilter === filter.value
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
              <div className="flex-1" />
              <button
                onClick={fetchExtractions}
                className="inline-flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
              >
                <RefreshCw size={16} className="mr-1" />
                Refresh
              </button>
            </div>
          </div>

          {/* Extractions Table */}
          {isLoadingExtractions ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : extractions.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <Globe className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No extractions found</h3>
              <p className="mt-2 text-gray-500">
                {statusFilter
                  ? 'No extractions match the selected filter.'
                  : 'Click "Extract Country" to start extracting CDC data.'}
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Country
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tokens
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Latency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Triggered By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Extracted
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {extractions.map((extraction) => {
                    const status = statusConfig[extraction.extraction_status] || statusConfig.processing;
                    const StatusIcon = status.icon;

                    return (
                      <tr
                        key={extraction.id}
                        onClick={() => navigate(`/cdc-extractions/${extraction.id}`)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-xs font-bold text-gray-600">
                              {extraction.country_iso2}
                            </span>
                            <div>
                              <p className="font-medium text-gray-900">{extraction.country_name}</p>
                              {extraction.is_current && (
                                <span className="text-xs text-green-600">Current</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.color}`}>
                            <StatusIcon size={14} className="mr-1" />
                            {status.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {extraction.llm_input_tokens
                            ? `${(extraction.llm_input_tokens / 1000).toFixed(1)}k / ${((extraction.llm_output_tokens || 0) / 1000).toFixed(1)}k`
                            : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {extraction.extraction_latency_ms
                            ? `${(extraction.extraction_latency_ms / 1000).toFixed(1)}s`
                            : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {extraction.triggered_by
                            ? extraction.triggered_by.split('@')[0]
                            : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(extraction.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {((extractionsPage - 1) * 20) + 1}-{Math.min(extractionsPage * 20, extractionsTotal)} of {extractionsTotal}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setExtractionsPage((p) => Math.max(1, p - 1))}
                  disabled={extractionsPage === 1}
                  className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm text-gray-600">
                  Page {extractionsPage} of {totalPages}
                </span>
                <button
                  onClick={() => setExtractionsPage((p) => Math.min(totalPages, p + 1))}
                  disabled={extractionsPage >= totalPages}
                  className="px-3 py-1 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {!isLoadingExtractions && extractions.length > 0 && totalPages <= 1 && (
            <div className="mt-4 text-sm text-gray-500 text-center">
              Showing {extractions.length} extraction{extractions.length !== 1 ? 's' : ''}
            </div>
          )}
        </>
      )}

      {/* Batches Tab */}
      {activeTab === 'batches' && (
        <>
          {/* Missing Countries Banner */}
          {!isLoadingMissing && missingCountries.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertCircle size={18} className="text-amber-600" />
                    <h3 className="font-semibold text-amber-900">
                      {missingCountries.length} countries missing extractions
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2 max-h-32 overflow-y-auto">
                    {missingCountries.map((c) => (
                      <span
                        key={c.country_iso2}
                        className="inline-flex items-center px-2 py-0.5 text-xs bg-amber-100 text-amber-800 rounded border border-amber-200"
                        title={c.country_name}
                      >
                        <span className="font-mono font-bold mr-1">{c.country_iso2}</span>
                        {c.country_name}
                      </span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={handleRetryMissing}
                  disabled={isRetrying}
                  className="ml-4 inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium disabled:opacity-50 whitespace-nowrap"
                >
                  {isRetrying ? (
                    <Loader2 size={16} className="mr-2 animate-spin" />
                  ) : (
                    <RefreshCw size={16} className="mr-2" />
                  )}
                  Retry Missing ({missingCountries.length})
                </button>
              </div>
            </div>
          )}

          {!isLoadingMissing && missingCountries.length === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2">
                <CheckCircle size={18} className="text-green-600" />
                <p className="text-green-800 font-medium">All countries have current extractions</p>
              </div>
            </div>
          )}

          <div className="flex justify-end mb-4">
            <button
              onClick={() => { fetchBatches(); fetchMissingCountries(); }}
              className="inline-flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
            >
              <RefreshCw size={16} className="mr-1" />
              Refresh
            </button>
          </div>

          {isLoadingBatches ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : batches.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <Layers className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No batch runs</h3>
              <p className="mt-2 text-gray-500">Click "Extract All" to trigger a batch extraction.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Triggered By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Started</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {batches.map((batch) => {
                    const bStatus = batchStatusConfig[batch.status] || batchStatusConfig.pending;
                    const progress = batch.total_countries > 0
                      ? Math.round(((batch.completed_countries || 0) + (batch.failed_countries || 0)) / batch.total_countries * 100)
                      : 0;

                    return (
                      <tr key={batch.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bStatus.color}`}>
                            {bStatus.label}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 w-32">
                              <div
                                className="bg-primary-600 h-2 rounded-full transition-all"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                            <span className="text-sm text-gray-600">
                              {batch.completed_countries || 0}/{batch.total_countries}
                              {(batch.failed_countries || 0) > 0 && (
                                <span className="text-red-600 ml-1">({batch.failed_countries} failed)</span>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {batch.triggered_by ? batch.triggered_by.split('@')[0] : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(batch.started_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {formatDate(batch.completed_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Supplemental Tab */}
      {activeTab === 'supplemental' && (
        <>
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            <div className="flex items-center space-x-4">
              <Filter size={18} className="text-gray-400" />
              <div className="flex flex-wrap gap-2">
                {[
                  { value: '', label: 'All', icon: null },
                  { value: 'altitude_sickness', label: 'Altitude Sickness', icon: Mountain },
                  { value: 'travelers_diarrhea', label: "Travelers' Diarrhea", icon: Droplets },
                ].map((filter) => (
                  <button
                    key={filter.value}
                    onClick={() => setSupplementalFilter(filter.value)}
                    className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      supplementalFilter === filter.value
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {filter.icon && <filter.icon size={14} className="mr-1.5" />}
                    {filter.label}
                  </button>
                ))}
              </div>
              <div className="flex-1" />
              <button
                onClick={fetchSupplemental}
                className="inline-flex items-center px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
              >
                <RefreshCw size={16} className="mr-1" />
                Refresh
              </button>
            </div>
          </div>

          {isLoadingSupplemental ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : supplementalData.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No supplemental data</h3>
              <p className="mt-2 text-gray-500">Upload CSV files for altitude sickness or travelers' diarrhea data.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Continent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Note</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Uploaded</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {supplementalData.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">{record.country_name}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${
                          record.data_type === 'altitude_sickness'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-cyan-100 text-cyan-800'
                        }`}>
                          {record.data_type === 'altitude_sickness' ? 'Altitude' : 'Diarrhea'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{record.continent || '-'}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-md truncate" title={record.additional_note}>
                        {record.additional_note || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatDate(record.uploaded_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500">
                {supplementalData.length} record{supplementalData.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </>
      )}

      {/* Trigger Country Modal */}
      {showTriggerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Extract Country</h3>
            <p className="text-gray-600 mb-4 text-sm">
              Enter a 2-letter ISO country code to trigger CDC data extraction.
            </p>
            <div className="flex items-center space-x-3 mb-3">
              <Search size={18} className="text-gray-400" />
              <input
                type="text"
                value={countryInput}
                onChange={(e) => setCountryInput(e.target.value.toUpperCase().slice(0, 2))}
                onKeyDown={(e) => e.key === 'Enter' && handleTriggerCountry()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 uppercase text-lg font-mono tracking-wider"
                placeholder="TH"
                maxLength={2}
                autoFocus
              />
            </div>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {[
                { code: 'TH', name: 'Thailand' },
                { code: 'BR', name: 'Brazil' },
                { code: 'KE', name: 'Kenya' },
                { code: 'IN', name: 'India' },
                { code: 'MX', name: 'Mexico' },
                { code: 'JP', name: 'Japan' },
                { code: 'ZA', name: 'South Africa' },
                { code: 'CO', name: 'Colombia' },
              ].map((c) => (
                <button
                  key={c.code}
                  onClick={() => setCountryInput(c.code)}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    countryInput === c.code
                      ? 'bg-primary-100 border-primary-300 text-primary-700'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                  title={c.name}
                >
                  {c.code} <span className="text-gray-400">{c.name}</span>
                </button>
              ))}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-800">
                The extraction will run asynchronously. Results typically appear within 1-2 minutes.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowTriggerModal(false); setCountryInput(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleTriggerCountry}
                disabled={countryInput.length !== 2 || isTriggeringCountry}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isTriggeringCountry ? (
                  <Loader2 size={18} className="inline mr-2 animate-spin" />
                ) : (
                  <Play size={18} className="inline mr-2" />
                )}
                Extract
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Trigger Modal */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Extract All Countries</h3>
            <p className="text-gray-600 mb-4 text-sm">
              This will trigger CDC extraction for every country with a CDC page. Each country is processed individually.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This may take 30+ minutes to complete for all countries. Progress can be tracked in the Batch Runs tab.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBatchModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleTriggerBatch}
                disabled={isTriggeringBatch}
                className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50"
              >
                {isTriggeringBatch ? (
                  <Loader2 size={18} className="inline mr-2 animate-spin" />
                ) : (
                  <Layers size={18} className="inline mr-2" />
                )}
                Extract All
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload CSV Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Supplemental CSV</h3>
            <p className="text-gray-600 mb-4 text-sm">
              Upload altitude sickness or travelers' diarrhea CSV data. Previous records of the same type will be replaced.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Type</label>
                <select
                  value={uploadDataType}
                  onChange={(e) => setUploadDataType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="altitude_sickness">Altitude Sickness</option>
                  <option value="travelers_diarrhea">Travelers' Diarrhea</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CSV File</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors text-center"
                >
                  {uploadFileName ? (
                    <div>
                      <FileText className="mx-auto h-8 w-8 text-primary-500 mb-2" />
                      <p className="text-sm font-medium text-gray-900">{uploadFileName}</p>
                      <p className="text-xs text-gray-500 mt-1">Click to change file</p>
                    </div>
                  ) : (
                    <div>
                      <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600">Click to select a CSV file</p>
                      <p className="text-xs text-gray-400 mt-1">Format: Country, Continent, Additional Note</p>
                    </div>
                  )}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowUploadModal(false); setUploadCsvContent(''); setUploadFileName(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadCsv}
                disabled={!uploadCsvContent || isUploading}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {isUploading ? (
                  <Loader2 size={18} className="inline mr-2 animate-spin" />
                ) : (
                  <Upload size={18} className="inline mr-2" />
                )}
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
